import { TelemetryPoint } from "../types";

/**
 * ELIOS 3 HARDWARE PROFILE CONSTANTS
 */
export const E3_HARDWARE_H_FOV = 114;
export const E3_HARDWARE_V_FOV = 82;
// The video is de-warped for 4K display, which crops ~12% of the hardware FOV edges.
export const E3_EFFECTIVE_FOV_MULTIPLIER = 0.88;
// The camera is mounted 25cm in front of the drone's center of mass (the telemetry origin).
export const E3_CAMERA_WALL_OFFSET = 0.25;

/**
 * Parses the SST Trajectory CSV format (space delimited)
 */
export const parseTrajectoryCsv = (csvText: string): TelemetryPoint[] => {
  const lines = csvText.trim().split('\n');
  const points: TelemetryPoint[] = [];

  const startIndex = lines[0].toLowerCase().includes('timestamp') ? 1 : 0;

  for (let i = startIndex; i < lines.length; i++) {
    const parts = lines[i].trim().split(/[\s,;]+/);
    if (parts.length < 4) continue; // Minimum: time, x, y, z

    points.push({
      timestamp: parseFloat(parts[0]),
      x: parseFloat(parts[1]),
      y: parseFloat(parts[2]),
      z: parseFloat(parts[3]),
      qx: parts.length > 7 ? parseFloat(parts[4]) : 0,
      qy: parts.length > 7 ? parseFloat(parts[5]) : 0,
      qz: parts.length > 7 ? parseFloat(parts[6]) : 0,
      qw: parts.length > 7 ? parseFloat(parts[7]) : 1,
      quality: parts.length > 8 ? parseFloat(parts[8]) : 1
    });
  }

  return points.sort((a, b) => a.timestamp - b.timestamp);
};

/**
 * Calculates physical surface coverage considering the concave nature of a cylinder.
 */
export const calculateSurfaceFootprint = (opticalStandoff: number, assetRadius: number) => {
  const effectiveH = E3_HARDWARE_H_FOV * E3_EFFECTIVE_FOV_MULTIPLIER;
  const effectiveV = E3_HARDWARE_V_FOV * E3_EFFECTIVE_FOV_MULTIPLIER;

  // Vertical Projection (Calibrated)
  const flatH = 2 * opticalStandoff * Math.tan(((effectiveV / 2) * Math.PI) / 180);

  // Concave Arc Calculation (Horizontal)
  const halfW = opticalStandoff * Math.tan(((effectiveH / 2) * Math.PI) / 180);
  const angleAtCenter = 2 * Math.atan(halfW / Math.max(0.01, assetRadius - opticalStandoff));
  const arcW = assetRadius * angleAtCenter;

  return {
    widthCm: Math.round(arcW * 100),
    heightCm: Math.round(flatH * 100),
    gsdMm: parseFloat(((arcW / 3840) * 1000).toFixed(2)) // 4K Horizontal GSD
  };
};

export const detectMissionStart = (points: TelemetryPoint[]): number => {
  if (!points || points.length === 0) return 0;
  const start = points[0];
  const MOVEMENT_THRESHOLD = 0.20;
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const dist = Math.sqrt(Math.pow(p.x - start.x, 2) + Math.pow(p.y - start.y, 2) + Math.pow(p.z - start.z, 2));
    if (dist > MOVEMENT_THRESHOLD) return points[i].timestamp;
  }
  return start.timestamp;
};

/**
 * Detects if a flight trajectory is predominantly horizontal (along X/Y) 
 * or vertical (along Z). Useful for adjusting AI spatial context.
 */
export const detectTrajectoryOrientation = (points: TelemetryPoint[]): 'HORIZONTAL' | 'VERTICAL' | 'STATIONARY' => {
  if (!points || points.length < 10) return 'STATIONARY';

  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  let minZ = Infinity, maxZ = -Infinity;

  points.forEach(p => {
    if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
    if (p.z < minZ) minZ = p.z; if (p.z > maxZ) maxZ = p.z;
  });

  const rangeX = maxX - minX;
  const rangeY = maxY - minY;
  const rangeZ = maxZ - minZ;

  // Max horizontal displacement (bounding box max dimension)
  const horizReach = Math.max(rangeX, rangeY);

  // STATIONARY: Very little movement in any direction
  if (horizReach < 0.3 && rangeZ < 0.3) return 'STATIONARY';

  // VERTICAL: Z-axis change is the dominant movement.
  // In NDT vertical strips, the height change is significantly larger than the X/Y drift.
  if (rangeZ > horizReach * 0.8) return 'VERTICAL';

  return 'HORIZONTAL';
};

/**
 * Shared utility to get the asset's wall radius at a specific altitude.
 */
export const getAssetRadiusAtZ = (z: number, geom: any) => {
  const { minZ, radius, cylinderBottomZ, coneBottomRadius } = geom;
  const coneHeight = (cylinderBottomZ || 0) - minZ;
  const relZ = z - minZ;

  if (coneHeight > 0 && relZ < coneHeight) {
    const bR = coneBottomRadius || 0.5;
    return bR + (radius - bR) * Math.max(0, relZ / coneHeight);
  }
  return radius;
};

/**
 * Filters a point cloud to only include points within the asset volume (plus a small tolerance).
 * Removes environmental noise and points outside the asset boundaries.
 */
export const filterPointsToAssetBoundary = (points: TelemetryPoint[], geom: any, tolerance: number = 0.5): TelemetryPoint[] => {
  if (!geom || !points || points.length === 0) return points;
  const { center, minZ, maxZ } = geom;

  return points.filter(p => {
    // 1. Z-Clipping
    if (p.z < minZ - tolerance || p.z > maxZ + tolerance) return false;

    // 2. Radial Clipping
    const dx = p.x - center.x;
    const dy = p.y - center.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const wallR = getAssetRadiusAtZ(p.z, geom);

    return dist <= wallR + tolerance;
  });
};

export const calculateBounds = (points: TelemetryPoint[]) => {
  if (!points || points.length === 0) return null;
  let minX = points[0].x, maxX = points[0].x;
  let minY = points[0].y, maxY = points[0].y;
  let minZ = points[0].z, maxZ = points[0].z;
  // Fix: Corrected erroneous loop condition 'i < i < points.length' to 'i < points.length'
  for (let i = 1; i < points.length; i++) {
    const p = points[i];
    if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
    if (p.z < minZ) minZ = p.z; if (p.z > maxZ) maxZ = p.z;
  }
  return { minX, maxX, minY, maxY, minZ, maxZ, rangeX: (maxX - minX) || 1, rangeY: (maxY - minY) || 1, rangeZ: (maxZ - minZ) || 1 };
};

const fitCircleRANSAC = (points: TelemetryPoint[], iterations: number = 300) => {
  if (points.length < 3) return { x: 0, y: 0, r: 5, score: 0 };
  let bestCircle = { x: 0, y: 0, r: 5, score: -1 };
  const tolerance = 0.5;
  const n = points.length;
  for (let i = 0; i < iterations; i++) {
    const p1 = points[Math.floor(Math.random() * n)], p2 = points[Math.floor(Math.random() * n)], p3 = points[Math.floor(Math.random() * n)];
    const D = 2 * (p1.x * (p2.y - p3.y) + p2.x * (p3.y - p1.y) + p3.x * (p1.y - p2.y));
    if (Math.abs(D) < 0.001) continue;
    const Ux = (1 / D) * ((p1.x ** 2 + p1.y ** 2) * (p2.y - p3.y) + (p2.x ** 2 + p2.y ** 2) * (p3.y - p1.y) + (p3.x ** 2 + p3.y ** 2) * (p1.y - p2.y));
    const Uy = (1 / D) * ((p1.x ** 2 + p1.y ** 2) * (p3.x - p2.x) + (p2.x ** 2 + p2.y ** 2) * (p1.x - p3.x) + (p3.x ** 2 + p3.y ** 2) * (p2.x - p1.x));
    const r = Math.sqrt((p1.x - Ux) ** 2 + (p1.y - Uy) ** 2);
    if (r < 1.0 || r > 20.0) continue;
    let score = 0;
    const step = n > 500 ? Math.ceil(n / 200) : 1;
    for (let j = 0; j < n; j += step) {
      const p = points[j];
      const d = Math.sqrt((p.x - Ux) ** 2 + (p.y - Uy) ** 2);
      if (Math.abs(d - r) < tolerance) score++;
    }
    if (score > bestCircle.score) bestCircle = { x: Ux, y: Uy, r: r, score: score };
  }
  return bestCircle;
};

export const calculateAssetGeometry = (points: TelemetryPoint[]) => {
  if (!points || points.length < 50) {
    const firstPointZ = points && points.length > 0 ? points[0].z : 0;
    return { radius: 5, center: { x: 0, y: 0 }, validPoints: [], minZ: 0, maxZ: 10, totalHeight: 10, cylinderBottomZ: 0, coneBottomRadius: 0.5, groundZ: firstPointZ };
  }
  const absoluteGroundZ = points[0].z;
  const sortedByZ = [...points].sort((a, b) => a.z - b.z);
  const startIdx = Math.floor(sortedByZ.length * 0.5);
  const cylinderCandidates = sortedByZ.slice(startIdx);
  const sampleStep = Math.ceil(cylinderCandidates.length / 1000);
  const samplePoints = [];
  for (let i = 0; i < cylinderCandidates.length; i += sampleStep) samplePoints.push(cylinderCandidates[i]);
  const ransacInput = samplePoints.length > 20 ? samplePoints : points;
  const ransacResult = fitCircleRANSAC(ransacInput, 300);
  const centerX = ransacResult.x, centerY = ransacResult.y;
  let cylinderRadius = ransacResult.r;
  const minZ_raw = sortedByZ[0].z, maxZ_raw = sortedByZ[sortedByZ.length - 1].z;
  const SLICE_H = 0.25, numSlices = Math.ceil((maxZ_raw - minZ_raw) / SLICE_H);
  const slices = new Array(numSlices).fill(0).map(() => [] as number[]);
  points.forEach(p => {
    const sIdx = Math.floor((p.z - minZ_raw) / SLICE_H);
    if (sIdx >= 0 && sIdx < numSlices) {
      const r = Math.sqrt(Math.pow(p.x - centerX, 2) + Math.pow(p.y - centerY, 2));
      slices[sIdx].push(r);
    }
  });
  const sliceWallRadii = new Array(numSlices).fill(null) as (number | null)[];
  slices.forEach((radii, idx) => {
    if (radii.length < 5) return;
    const bins: { [key: number]: number } = {};
    const BIN_SIZE = 0.2;
    let maxCount = 0, bestBin = 0;
    radii.forEach(r => {
      const b = Math.floor(r / BIN_SIZE);
      bins[b] = (bins[b] || 0) + 1;
      if (bins[b] > maxCount) { maxCount = bins[b]; bestBin = b; }
    });
    const ptsInBin = radii.filter(r => Math.floor(r / BIN_SIZE) === bestBin);
    if (ptsInBin.length > 0) { ptsInBin.sort((a, b) => a - b); sliceWallRadii[idx] = ptsInBin[Math.floor(ptsInBin.length / 2)]; }
  });
  for (let i = 1; i < numSlices - 1; i++) if (sliceWallRadii[i] === null && sliceWallRadii[i - 1] !== null && sliceWallRadii[i + 1] !== null) sliceWallRadii[i] = (sliceWallRadii[i - 1]! + sliceWallRadii[i + 1]!) / 2;
  const validPoints: TelemetryPoint[] = [];
  const LOCAL_TOLERANCE = 0.8;
  let filteredMinZ = Infinity, filteredMaxZ = -Infinity;
  points.forEach(p => {
    const sIdx = Math.floor((p.z - minZ_raw) / SLICE_H);
    if (sIdx >= 0 && sIdx < numSlices) {
      const wallR = sliceWallRadii[sIdx];
      if (wallR !== null) {
        const r = Math.sqrt(Math.pow(p.x - centerX, 2) + Math.pow(p.y - centerY, 2));
        if (Math.abs(r - wallR) < LOCAL_TOLERANCE) {
          validPoints.push(p);
          if (p.z < filteredMinZ) filteredMinZ = p.z;
          if (p.z > filteredMaxZ) filteredMaxZ = p.z;
        }
      }
    }
  });
  if (validPoints.length === 0) return { radius: 5, center: { x: centerX, y: centerY }, validPoints: points, minZ: minZ_raw, maxZ: maxZ_raw, totalHeight: maxZ_raw - minZ_raw, cylinderBottomZ: minZ_raw, coneBottomRadius: 0.5, groundZ: absoluteGroundZ };
  const upperRadii = sliceWallRadii.slice(Math.floor(numSlices / 2)).filter(r => r !== null) as number[];
  if (upperRadii.length > 5) { upperRadii.sort((a, b) => a - b); cylinderRadius = upperRadii[Math.floor(upperRadii.length / 2)]; }
  let cylinderBottomZ = filteredMinZ, inCylinder = true;
  for (let i = numSlices - 1; i >= 0; i--) {
    const z = minZ_raw + (i * SLICE_H), r = sliceWallRadii[i];
    if (r === null) continue;
    if (inCylinder && r < cylinderRadius * 0.85) {
      let confirm = 0;
      for (let k = 1; k <= 3; k++) if (i - k >= 0 && sliceWallRadii[i - k] !== null && sliceWallRadii[i - k]! < cylinderRadius * 0.85) confirm++;
      if (confirm >= 2) { cylinderBottomZ = z + SLICE_H; inCylinder = false; break; }
    }
  }
  let estConeBottomRadius = 0.5;
  for (let i = 0; i < numSlices; i++) if (sliceWallRadii[i] !== null && (minZ_raw + i * SLICE_H) < cylinderBottomZ + 0.1) { estConeBottomRadius = sliceWallRadii[i]!; break; }
  return { center: { x: centerX, y: centerY }, radius: parseFloat(cylinderRadius.toFixed(2)), validPoints, minZ: filteredMinZ, maxZ: filteredMaxZ, totalHeight: filteredMaxZ - filteredMinZ, cylinderBottomZ, coneBottomRadius: parseFloat(estConeBottomRadius.toFixed(2)), groundZ: absoluteGroundZ };
};

export const parseLasFile = (file: File): Promise<TelemetryPoint[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const buffer = e.target?.result as ArrayBuffer; if (!buffer) return reject("Read error");
        const view = new DataView(buffer);
        if (view.getUint8(0) !== 76 || view.getUint8(1) !== 65 || view.getUint8(2) !== 83 || view.getUint8(3) !== 70) return reject("Invalid LAS signature");
        const offsetToPointData = view.getUint32(96, true), pointRecordLength = view.getUint16(105, true), numberOfPoints = view.getUint32(107, true);
        const scaleX = view.getFloat64(131, true), scaleY = view.getFloat64(139, true), scaleZ = view.getFloat64(147, true);
        const offsetX = view.getFloat64(155, true), offsetY = view.getFloat64(163, true), offsetZ = view.getFloat64(171, true);
        const points: TelemetryPoint[] = [];
        const skip = Math.max(1, Math.ceil(numberOfPoints / 30000));
        for (let i = 0; i < numberOfPoints; i += skip) {
          const pOffset = offsetToPointData + (i * pointRecordLength);
          if (pOffset + 12 > buffer.byteLength) break;
          const X = view.getInt32(pOffset, true), Y = view.getInt32(pOffset + 4, true), Z = view.getInt32(pOffset + 8, true);
          points.push({ x: (X * scaleX) + offsetX, y: (Y * scaleY) + offsetY, z: (Z * scaleZ) + offsetZ, timestamp: 0, qx: 0, qy: 0, qz: 0, qw: 1, quality: 1, scalar: 0.5 + (Math.random() * 0.1) });
        }
        resolve(points);
      } catch (err) { reject(err); }
    };
    reader.readAsArrayBuffer(file);
  });
};

export const parseTextPointCloud = (file: File): Promise<TelemetryPoint[]> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n'), points: TelemetryPoint[] = [];
      const step = Math.max(1, Math.ceil(lines.length / 30000));
      for (let i = 0; i < lines.length; i += step) {
        const line = lines[i].trim(); if (!line || /^[a-zA-Z#]/.test(line)) continue;
        const parts = line.split(/[\s,;]+/);
        if (parts.length >= 3) {
          const x = parseFloat(parts[0]), y = parseFloat(parts[1]), z = parseFloat(parts[2]);
          if (!isNaN(x) && !isNaN(y) && !isNaN(z)) points.push({ x, y, z, timestamp: 0, qx: 0, qy: 0, qz: 0, qw: 1, quality: 1, scalar: 0.6 });
        }
      }
      resolve(points);
    };
    reader.readAsText(file);
  });
};